<script setup lang="ts">
  import { computed, watch, onMounted } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import type { GenericObject } from 'vee-validate'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import InputField from '@/components/admin/form/InputField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import { useUserStore } from '@/store/userStore'

  const userStore = useUserStore()

  const props = defineProps<{
    caseData: CustomerCase
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['update:payload'])

  const { t } = useTranslation({
    en: {
      datapool: 'Datapool',
      datapoolRequired: 'Datapool is required',
      rubrum: 'Short Rubrum',
      rubrumRequired: 'Short Rubrum is required',
      rubrumInvalidCharacters:
        'Short Rubrum contains invalid characters. Only Latin-1 characters are allowed.',
      lawyers: 'Lawyers',
      lawyersRequired: 'At least one lawyer should be selected',
      lawCategory: 'Law Category',
      lawCategoryRequired: 'Law Category is required',
      recordSchema: 'Record Schema',
      recordSchemaRequired: 'Record Schema is required',
      jurnodesSection: 'Jurnodes Information'
    },
    de: {
      datapool: 'Datenpool',
      datapoolRequired: 'Datenpool ist erforderlich',
      rubrum: 'Kurzrubrum',
      rubrumRequired: 'Kurzrubrum ist erforderlich',
      rubrumInvalidCharacters:
        'Kurzrubrum enthält ungültige Zeichen. Nur Latin-1 Zeichen sind erlaubt.',
      lawyers: 'Aktenverantwortlichen',
      lawyersRequired: 'Mindestens ein Aktenverantwortlicher sollte ausgewählt werden',
      lawCategory: 'Rechtsgebiet',
      lawCategoryRequired: 'Rechtsgebiet ist erforderlich',
      recordSchema: 'Verfahrensschema',
      recordSchemaRequired: 'Verfahrensschema ist erforderlich',
      jurnodesSection: 'Jurnodes Information'
    }
  })

  const defaultRubrum = computed(() => {
    const { law, client, opponent } = props.caseData.data

    if (law.field === 'Strafrecht') {
      return `${client.first_name} ${client.last_name}`
    }

    const clientName =
      client.is_company && client.company_name
        ? `${client.company_name} ${client.company_legal_form || 'GmbH'}`
        : client.last_name

    if (!opponent) {
      return `${clientName} ./.`
    }

    const opponentName =
      opponent.is_company && opponent.company_name
        ? `${opponent.company_name} ${opponent.company_legal_form || 'GmbH'}`
        : opponent.last_name

    return `${clientName} ./. ${opponentName}`
  })

  watch(
    () => defaultRubrum.value,
    (newValue) => {
      formData.rubrum = newValue
    },
    { deep: true }
  )

  onMounted(() => {
    handleSave({
      datapool: null,
      rubrum: defaultRubrum.value,
      lawyers: [],
      lawCategory: null,
      recordSchema: null
    })
  })

  const jurnodesDatapoolOptions = computed(() =>
    (userStore.user?.org?.jurnodes_datapool_options || []).map(
      (option: { id: number; name: string }) => ({
        value: option.id,
        title: option.name
      })
    )
  )

  const jurnodesLawyerOptions = computed(() =>
    (userStore.user?.org?.jurnodes_lawyer_options || []).map(
      (option: { id: number; name: string }) => ({
        value: option.id,
        title: option.name
      })
    )
  )

  const jurnodesLawCategoryOptions = computed(() =>
    (userStore.user?.org?.jurnodes_law_category_options || []).map(
      (option: { id: number; name: string }) => ({
        value: option.id,
        title: option.name
      })
    )
  )

  const jurnodesRecordSchemaOptions = computed(() =>
    (userStore.user?.org?.jurnodes_record_schema_options || []).map(
      (option: { id: number; name: string }) => ({
        value: option.id,
        title: option.name
      })
    )
  )

  // Latin-1 characters excluding control characters (0x00-0x1F and 0x7F-0x9F)
  const latin1Regex = /^[\u0020-\u007E\u00A0-\u00FF]*$/

  const schema = {
    datapool: yup.string().required(t('datapoolRequired')),
    rubrum: yup
      .string()
      .required(t('rubrumRequired'))
      .matches(latin1Regex, t('rubrumInvalidCharacters')),
    lawyers: yup.array().of(yup.number().integer().notRequired()).min(1, t('lawyersRequired')),
    lawCategory: yup.number().nullable().required(t('lawCategoryRequired')),
    recordSchema: yup.number().nullable().required(t('recordSchemaRequired'))
  }

  interface FormData {
    datapool: number
    rubrum: string
    lawyers: Array<number>
    lawCategory: number
    recordSchema: number
  }

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors
  } = useFormWithValidation<FormData>(
    {
      datapool: jurnodesDatapoolOptions.value[0]?.value,
      rubrum: defaultRubrum.value,
      lawyers: [],
      lawCategory: null as unknown as number,
      recordSchema: null as unknown as number // a way to force number to be null instead of using NaN
    },
    schema
  )

  defineExpose({
    validate,
    showErrors
  })

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:payload', updatedData)
  }

  watch(
    formData,
    (newValue) => {
      handleSave(newValue)
    },
    { deep: true }
  )
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold">{{ t('jurnodesSection') }}</h2>
    <AutoSavingForm
      :debounce-delay="debounceDelay"
      :throttle-interval="throttleInterval"
      class="space-y-6"
      @save="handleSave"
    >
      <div class="space-y-4">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('datapool') }}
          </label>
          <SelectField
            v-model="formData.datapool"
            :options="jurnodesDatapoolOptions"
            name="datapool"
            :placeholder="t('datapool')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.datapool ?? null"
            :rules="schema.datapool"
            @update:model-value="(value: any) => handleFieldChange('datapool', value)"
            @focus="() => handleFieldTouch('datapool')"
          />
        </div>
        <div>
          <InputField
            v-model="formData.rubrum"
            :label="t('rubrum')"
            name="rubrum"
            :error="formErrors?.value?.rubrum ?? null"
            :rules="schema.rubrum"
            @update:model-value="(value: any) => handleFieldChange('title', value)"
            @focus="() => handleFieldTouch('title')"
          />
        </div>
        <div>
          <MultiSelectField
            v-model="formData.lawyers"
            :label="t('lawyers')"
            :options="jurnodesLawyerOptions"
            name="lawyers"
            :error="formErrors?.value?.lawyers ?? null"
            :rules="schema.lawyers"
            @update:model-value="(value: any) => handleFieldChange('lawyers', value)"
            @focus="() => handleFieldTouch('lawyers')"
          />
        </div>
        <div>
          <SelectField
            v-model="formData.lawCategory"
            :label="t('lawCategory')"
            :options="jurnodesLawCategoryOptions"
            name="lawCategory"
            :placeholder="t('lawCategory')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.lawCategory ?? null"
            :rules="schema.lawCategory"
            @update:model-value="(value: any) => handleFieldChange('lawCategory', value)"
            @focus="() => handleFieldTouch('lawCategory')"
          />
        </div>
        <div>
          <SelectField
            v-model="formData.recordSchema"
            :label="t('recordSchema')"
            :options="jurnodesRecordSchemaOptions"
            name="recordSchema"
            :placeholder="t('recordSchema')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.recordSchema ?? null"
            :rules="schema.recordSchema"
            @update:model-value="(value: any) => handleFieldChange('recordSchema', value)"
            @focus="() => handleFieldTouch('recordSchema')"
          />
        </div>
      </div>
    </AutoSavingForm>
  </div>
</template>
